import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { DebounceInput } from "react-debounce-input";
import {
  CellInfo,
  TableCellRenderer
} from "react-table";
import styled from "styled-components";
import { $enum } from "ts-enum-util";

import {
  Datetime,
  DateTemplatesType
} from "src/library/components/Datetime";
import { DayRangePicker } from "src/library/components/DayRangePicker";
import {
  Select,
  IFormOption
} from "src/library/components/Forms";
import { CustomTableColumn } from "src/library/components/Tables/Table";
import { Text } from "src/library/components/Text";
import { sentenceCase } from "src/library/utils/string-manipulations";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Link } from "src/library/components/Link";
import { OrderTypeText } from "src/core/orders/components/OrderTypeText";
import OrderStatus from "src/core/orders/components/OrderStatus";
import ProductStatus from "src/core/products/components/ProductStatus";
import { getScheduleNaturalLanguage } from "src/core/schedules/utils";
import {
  IOrderLine,
  IOrderStatus,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import {
  IListLine,
  IList
} from "src/core/lists/api/lists.api";
import { transactionTypeToHuman } from "src/modules/payment/utils";
import { TransactionStatusText } from "src/modules/payment/components/TransactionStatusText";
import { orderPaymentStatusToHuman } from "src/modules/payment/utils/order-payment-status-to-human";
import { OrderPaymentStatusText } from "src/modules/payment/components/OrderPaymentStatusText";
import SupportTicketStatusStyled, { statusTextSwitch } from "src/modules/support/components/SupportTicketStatus";
import type { IStore } from "src/core/shared/store";
import {
  SupportTicketStatus,
  CreditStatus
} from "src/modules/support/api/support.api";
import { useCurrentUser } from "src/core/users/hooks";
import CreditRequestStatusStyled, { creditStatusTextSwitch } from "src/modules/support/components/CreditStatus";
import {
  CustomerDropStatuses,
  ICustomer
} from "src/core/customers/api/customers.api";
import { IPriceCode } from "src/core/shared/api/price-change.api";
import { ScheduleStatusText } from "src/core/lists/components/ScheduleStatusText";
import Status from "src/library/components/Status/Status";
import { Tooltip } from "src/library/components/Tooltip";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { PriorityIcon } from "src/modules/support/components/SupportPriorityDropdown";
import AssignedUser from "src/library/components/AssignedUser/AssignedUser";
import CustomerDropStatus from "src/core/customers/components/CustomerDropStatus";
import { normalizeDateRangeForTimezone } from "src/library/utils/moment/normalizeDateRangeForTimezone";

interface IIdProps {
  header?: string;
  width?: number;
  show?: boolean;
  accessor?: string;
  sortable?: boolean;
}
interface ICheckboxProps {
  name: string;
  header: React.ReactElement;
  cell: TableCellRenderer;
  id: string;
  show?: boolean;
}

interface IActionsProps {
  header?: string;
  cell: TableCellRenderer;
  width?: number;
  mainAction?: (row: CellInfo) => void;
}

interface IDateProps extends CustomTableColumn {
  header: string;
  accessor: string;
  width?: number;
  fallback?: React.ReactElement;
  format?: string;
  template?: DateTemplatesType
  ignoreLocal?: boolean;
}

/**
 * Checkbox column
 */
export const checkbox = ({
  header, cell, id, show
}: ICheckboxProps): CustomTableColumn => {
  return {
    Header: header,
    id,
    headerStyle: { textAlign: "center" },
    style: { textAlign: "center" },
    width: 80,
    sortable: false,
    filterable: false,
    Cell: cell,
    show
  };
};

/**
 * Record ID column
 */
export const id = (props?: IIdProps): CustomTableColumn => {
  return {
    Header: props?.header ? props.header : "Id",
    accessor: props?.accessor || "id",
    width: props?.width || 50,
    show: props?.show || false,
    Filter: ({ onChange, filter }) => (
      <DebounceInput
        debounceTimeout={1500}
        value={filter ? filter.value : ""}
        onChange={e => onChange(e.target.value)}
      />
    )
  };
};

/**
 * Custom column
 */
export const customer = (terminology: ITerminology, accessor = "branch.customer"): CustomTableColumn => {
  return {
    Header: `${sentenceCase(terminology.customer.singular.text)}`,
    accessor,
    width: 150,
    id: "customer",
    sortable: false,
    Cell: ({ value }) => {
      if (value) {
        const customer = value as ICustomer;

        if (customer) {
          return `(${customer.accountNumber}) ${customer.name}`;
        }
      }

      return "-";
    },
    Filter: ({ onChange, filter }) => (
      <DebounceInput
        debounceTimeout={1500}
        minLength={2}
        value={filter ? filter.value : ""}
        onChange={e => onChange(e.target.value)}
      />
    )
  };
};

/**
 * Branch column
 */
export const branch = (terminology: ITerminology): CustomTableColumn => {
  return {
    Header: `${sentenceCase(terminology.drop.singular.text)}`,
    accessor: "branch",
    width: 150,
    id: "branch",
    sortable: false,
    Cell: ({ original: row }) => {
      if (row.branch) {
        return `(${row.branch.branchNumber ? row.branch.branchNumber : "-"}) ${row.branch.name ? row.branch.name : ""}`;
      }

      return "-";
    },
    Filter: ({ onChange, filter }) => (
      <DebounceInput
        debounceTimeout={1500}
        minLength={2}
        value={filter ? filter.value : ""}
        onChange={e => onChange(e.target.value)}
      />
    )
  };
};

/**
 * Order lines count column
 */
export const orderLinesCount = (terminology: ITerminology): CustomTableColumn => {
  return {
    Header: () => (
      <div style={{ textAlign: "center" }}>
        {`${sentenceCase(terminology.order.singular.text)} lines`}
      </div>
    ),
    style: { textAlign: "center" },
    sortable: false,
    filterable: false,
    id: "orderLinesCount",
    Cell: ({ original: row }) => row.orderLines.length
  };
};

/**
 * Product count column
 */
export const productCount = (terminology: ITerminology): CustomTableColumn => {
  return {
    Header: () => (
      <div style={{ textAlign: "center" }}>
        {sentenceCase(terminology.product.plural.text)}
      </div>
    ),
    style: { textAlign: "center" },
    sortable: false,
    filterable: false,
    width: 80,
    id: "productCount",
    Cell: ({ original: row }) => {
      const orderLines: Array<IOrderLine | IListLine> = row.orderLines || row.listLines;
      let count = 0;

      orderLines.map(orderLine => {
        if (!(orderLine as IOrderLine).substituteOrderLineId) {
          count += orderLine.quantity;
        }
      });

      return count;
    }
  };
};

export const NoWrapCell = styled.div`
  padding: 20px 0;
  white-space: pre-wrap;
  > span {
    display: block;
    &::first-letter {
      text-transform: uppercase;
    }
  }
`;

/**
 * Schedule natural language data
 */
export const scheduleData = (terminology: ITerminology): CustomTableColumn => {
  return {
    Header: terminology.schedule.singular.text,
    accessor: "schedule",
    filterable: false,
    sortable: false,
    Cell: (cellProps: CellInfo) => {
      const list: IList = cellProps.original;

      if (list && list.schedule) {
        const { frequencyDays, startAndEndDates } = getScheduleNaturalLanguage(list.schedule, list.hasDetachedOrders || false, terminology);

        return (
          <NoWrapCell>
            <Text>
              {frequencyDays}
            </Text>

            <div style={{ paddingBottom: "20px" }} />

            <Text>
              {startAndEndDates}
            </Text>
          </NoWrapCell>
        );
      } else {
        return `No ${terminology.schedule.singular.text}`;
      }
    }
  };
};

/**
 * Schedule status
 */
export const scheduleStatus = (terminology: ITerminology): CustomTableColumn => {
  return {
    Header: `${terminology.schedule.singular.text} Status`,
    accessor: "schedule-status",
    filterable: false,
    sortable: false,
    Cell: (cellProps: CellInfo) => {
      const list: IList = cellProps.original;

      if (list.schedule) {
        if (list) {
          return (
            <ScheduleStatusText
              status={list.schedule.status}
              hasErrors={list.errors && !!list.errors.length}
            />
          );
        }
      }

      return "-";
    }
  };
};

/**
 * No-specific data column
 */
export const date = ({
  format, template, header, accessor, width, fallback, ignoreLocal, id, ...rest
}: IDateProps): CustomTableColumn => {
  return {
    ...rest,
    Header: header,
    accessor,
    width: template === "datetime" ? 160 : width || 100,
    Cell: cellProps => {
      if (cellProps.value) {
        return (
          <Datetime
            ignoreLocal={ignoreLocal}
            format={format}
            template={template}
            date={cellProps.value}
          />
        );
      } else if (fallback) {
        return (
          <Text>
            {fallback}
          </Text>
        );
      }

      return null;
    },
    id: id || accessor,
    Filter: ({ onChange, filter }) => (
      <DayRangePicker
        initialDateRange={filter && filter.value}
        includeInput
        date={moment.utc().toDate()}
        minDate={moment
          .utc()
          .subtract(10, "years")
          .toDate()}
        maxDate={moment
          .utc()
          .add(10, "years")
          .toDate()}
        handleDateChange={({ from, to }) => {
          onChange({
            from: moment.utc(from).startOf("day").toDate(),
            to: moment.utc(to).endOf("day").toDate()
          });
        }}
      />
    )
  };
};

export const status = (): CustomTableColumn => {
  return {
    Header: "Status",
    accessor: "status",
    id: "status",
    width: 120,
    sortable: false,
    Cell: cellProps => <Status status={cellProps.value} />,
    filterable: false
  };
};

export const customerDropStatus = (): CustomTableColumn => {
  return {
    Header: "Status",
    accessor: "status",
    id: "status",
    width: 120,
    filterable: true,
    sortable: false,
    Cell: cellProps => <CustomerDropStatus status={cellProps.value} />,
    Filter: ({ onChange, filter }) => {
      return (
        <Select
          value={filter ? filter.value : ""}
          label=""
          as="select"
          name="status"
          unselectedOption={{
            text: "Please Select",
            value: ""
          }}
          handleChange={e => onChange(e.target.value)}
          options={$enum(CustomerDropStatuses).getValues().map(status => ({
            text: status.replace(/-/g, " "),
            value: status
          }))}
        />
      );
    }
  };
};

export const productStatus = (): CustomTableColumn => {
  return {
    Header: "Status",
    accessor: "currentStatus.status",
    id: "status",
    width: 120,
    sortable: false,
    Cell: cellProps => <ProductStatus status={cellProps.value} />,
    filterable: false
  };
};

/**
 * Order status column
 */
export const orderStatus = (orderStatuses: IOrderStatus[]): CustomTableColumn => {
  return {
    Header: "Status",
    accessor: "orderStatus",
    id: "orderStatus",
    width: 150,
    sortable: false,
    Cell: cellProps => (
      <OrderStatus
        orderId={cellProps.original.id}
        status={cellProps.value}
        tooltipPlace="right"
      />
    ),
    Filter: ({ onChange, filter }) => {
      return (
        <Select
          value={filter ? filter.value : ""}
          label=""
          as="select"
          name="status"
          unselectedOption={{
            text: "Please Select",
            value: ""
          }}
          handleChange={e => onChange(e.target.value)}
          options={_.map(orderStatuses, status => ({
            text: status.name,
            value: status.id.toString(),
            checked: filter && filter.value === status.id
          }))}
        />
      );
    }
  };
};

/**
 * Order status column
 */
export const orderType = (): CustomTableColumn => {
  return {
    Header: "Type",
    accessor: "type",
    id: "type",
    width: 150,
    sortable: false,
    Cell: cellProps => <OrderTypeText type={cellProps.value} />,
    Filter: ({ onChange, filter }) => {
      return (
        <Select
          value={filter ? filter.value : ""}
          label=""
          as="select"
          name="status"
          unselectedOption={{
            text: "Please Select",
            value: ""
          }}
          handleChange={e => onChange(e.target.value)}
          options={[
            {
              text: "Ad hoc",
              value: "adhoc",
              checked: filter && filter.value === "adhoc"
            },
            {
              text: "Standing order",
              value: "scheduled",
              checked: filter && filter.value === "scheduled"
            },
            {
              text: "Bulk Order",
              value: "bulk",
              checked: filter && filter.value === "bulk"
            }
          ]}
        />
      );
    }
  };
};

/**
 * Transaction type column
 */
export const transactionType = (): CustomTableColumn => {
  return {
    Header: "Type",
    accessor: "type",
    width: 100,
    filterable: false,
    Cell: (cellProps: CellInfo) => {
      return (
        <Text>
          {transactionTypeToHuman(cellProps.value)}
        </Text>
      );
    }
  };
};

/**
 * Transaction status column
 */
export const transactionStatus = (): CustomTableColumn => {
  return {
    Header: "Status",
    accessor: "status",
    width: 100,
    filterable: false,
    Cell: (cellProps: CellInfo) => <TransactionStatusText status={cellProps.value} />
  };
};

/**
 * Transaction order ID column
 */
export const transactionOrderId = (): CustomTableColumn => {
  return {
    Header: "Order #",
    accessor: "orderId",
    width: 100,
    Cell: (cellProps: CellInfo) => (
      <Link
        className="value"
        to={`/orders/${cellProps.value}`}
      >
        {cellProps.value}
      </Link>
    )
  };
};

/**
 * Record ID column
 */
export const actions = ({
  width, cell, mainAction
}: IActionsProps): CustomTableColumn => {
  return {
    Header: "Actions",
    id: "actions",
    width: width || 120,
    headerStyle: { textAlign: "center" },
    sortable: false,
    filterable: false,
    Cell: cell,
    mainAction,
    show: true,
    mobile: false,
    resizable: false
  };
};

export interface StandardTextColumn extends CustomTableColumn {
  width?: number;
  minLength?: number;
  textTransform?: React.CSSProperties["textTransform"];
  multiLine?: boolean;
}

export const standardText = (props: StandardTextColumn): CustomTableColumn => {
  return {
    ...props,
    width: props.width || 150,
    Cell: props.Cell ?
      props.Cell :
      (info: CellInfo) => (
        <Text
          style={props.multiLine ? { whiteSpace: "pre-wrap" } : {}}
          textTransform={props.textTransform}
        >
          {info.value}
        </Text>
      ),
    Filter: props.Filter ? props.Filter : ({ onChange, filter }) => (
      <DebounceInput
        debounceTimeout={1500}
        minLength={props.minLength || 2}
        value={filter ? filter.value : ""}
        onChange={e => onChange(e.target.value)}
      />
    )
  };
};

export const orderPaymentStatusColumn = standardText({
  Header: "Payment Status",
  accessor: "paymentStatus",
  sortable: false,
  Filter: ({ onChange, filter }) => {
    return (
      <Select
        value={filter ? filter.value : ""}
        label=""
        as="select"
        name="status"
        unselectedOption={{
          text: "Please Select",
          value: ""
        }}
        handleChange={e => onChange(e.target.value)}
        options={_.map($enum(OrderPaymentStatus).getValues(), status => {
          return ({
            text: orderPaymentStatusToHuman(status),
            value: status,
            checked: filter && filter.value === status
          });
        })}
      />
    );
  },
  Cell: row => {
    if (!row.value) {
      return "-";
    }

    return <OrderPaymentStatusText status={row.value} />;
  }
});

/**
 * Helper function for getting the support ticket status column
 * @returns Selector with all support ticket status
 */
export const SupportTicketStatusColumn = (): CustomTableColumn => {
  const [ { currentRole } ] = useCurrentUser();

  return {
    Header: "Status",
    accessor: "status",
    id: "status",
    width: 150,
    sortable: false,
    Cell: cellProps => <SupportTicketStatusStyled status={cellProps.value} />,
    Filter: ({ onChange, filter }) => {
      const options = [
        {
          text: statusTextSwitch(SupportTicketStatus.PendingAdminResponse, currentRole?.type),
          value: SupportTicketStatus.PendingAdminResponse,
          checked: filter && filter.value.toString() === SupportTicketStatus.PendingAdminResponse
        },
        {
          text: statusTextSwitch(SupportTicketStatus.PendingCustomerResponse, currentRole?.type),
          value: SupportTicketStatus.PendingCustomerResponse,
          checked: filter && filter.value.toString() === SupportTicketStatus.PendingCustomerResponse
        },
        {
          text: statusTextSwitch(SupportTicketStatus.New, currentRole?.type),
          value: SupportTicketStatus.New,
          checked: filter && filter.value.toString() === SupportTicketStatus.New
        },
        {
          text: statusTextSwitch(SupportTicketStatus.AdminResolved, currentRole?.type),
          value: "resolved",
          checked: filter && filter.value.toString() === "resolved"
        }
      ];

      return (
        <Select
          label=""
          as="select"
          name="status"
          handleChange={e => onChange(e.target.value)}
          options={options}
          unselectedOption={{
            text: "Please Select",
            value: ""
          }}
          value={filter ? filter.value : ""}
        />
      );
    }
  };
};

/**
 * Helper function for getting the support ticket assigned column
 * @returns assigned user and user group in one component
 */
export const userColumn = (accessor: string, name: string): CustomTableColumn => {
  return {
    Header: name,
    accessor,
    id: accessor,
    filterable: false,
    sortable: false,
    Cell: cellProps => {
      const user = cellProps.original ? cellProps.original[ accessor ] : null;

      return (
        <AssignedUser
          rowId={cellProps.original?.id}
          user={user}
          groupName={cellProps.original?.userGroup?.name}
        />
      );
    }
  };
};

/**
 * Helper function for getting the support ticket priority column
 */
export const SupportTicketPriorityColumn = (): CustomTableColumn => {
  return {
    Header: () => {
      return (
        <Icon
          icon={Icons.PRIORITY}
          size={20}
        />
      );
    },
    accessor: "priority",
    id: "priority",
    filterable: false,
    resizable: false,
    width: 40,
    sortable: true,
    Cell: cellProps => {
      return (
        <>
          <div
            data-tip
            data-for={cellProps.value}
          >
            <PriorityIcon level={cellProps.value} />
          </div>

          <Tooltip
            id={cellProps.value}
            type="dark"
            place="right"
            getContent={() => {
              return (
                <Text
                  color="white"
                  textTransform="capitalize"
                >
                  {`${cellProps.value} priority`}
                </Text>
              );
            }}
          />
        </>
      );
    }
  };
};

/**
 * Helper function for getting the support ticket issue type column
 * @returns Selector with all support ticket types
 */
export const SupportTicketIssueType = (): CustomTableColumn => {
  // Get support ticket types from redux
  const { supportTicketTypes } = useMappedState((store: IStore) => ({ supportTicketTypes: store.global.settings && store.global.settings.supportTicketTypes }));

  return {
    Header: "Issue Type",
    accessor: "supportTicketType.name",
    id: "supportTicketTypeId",
    sortable: false,
    width: 140,
    Cell: cellProps => (
      <Text>
        {cellProps.value}
      </Text>
    ),
    Filter: ({ onChange, filter }) => {
      const options: IFormOption[] = supportTicketTypes?.map(type => ({
        text: type.name,
        value: type.id.toString(),
        checked: filter && filter.value == type.id
      })) || [];

      return (
        <Select
          label=""
          as="select"
          name="status"
          handleChange={e => onChange(e.target.value)}
          options={options}
          unselectedOption={{
            text: "Please Select",
            value: ""
          }}
          value={filter ? filter.value : ""}
        />
      );
    }
  };
};

/**
 * Helper function for getting the credit status column from Support ticket
 * @returns Selector with all credit request statuses
 */
export const CreditStatusColumn = (): CustomTableColumn => {
  return {
    Header: "Status",
    accessor: "creditStatus",
    id: "creditStatus",
    width: 150,
    sortable: false,
    Cell: cellProps => <CreditRequestStatusStyled status={cellProps.value} />,
    Filter: ({ onChange, filter }) => {
      const options = [
        {
          text: creditStatusTextSwitch(CreditStatus.Reviewed),
          value: CreditStatus.Reviewed,
          checked: filter && filter.value.toString() === CreditStatus.Reviewed
        },
        {
          text: creditStatusTextSwitch(CreditStatus.AutoReviewed),
          value: CreditStatus.AutoReviewed,
          checked: filter && filter.value.toString() === CreditStatus.AutoReviewed
        },
        {
          text: creditStatusTextSwitch(CreditStatus.RequiresReview),
          value: CreditStatus.RequiresReview,
          checked: filter && filter.value.toString() === CreditStatus.RequiresReview
        }
      ];

      return (
        <Select
          label=""
          as="select"
          name="status"
          handleChange={e => onChange(e.target.value)}
          options={options}
          unselectedOption={{
            text: "Please Select",
            value: ""
          }}
          value={filter ? filter.value : ""}
        />
      );
    }
  };
};

/**
 * Price code name & code
 */
export const priceCodeNameCode = (): CustomTableColumn => {
  return {
    Header: "Price Code",
    accessor: "priceCode",
    filterable: true,
    sortable: false,
    Cell: (cellProps: CellInfo) => {
      const priceCode: IPriceCode = cellProps.original.priceCode;

      if (priceCode) {
        return (
          <Text>
            {`${priceCode.name} (${priceCode.code})`}
          </Text>
        );
      }

      return "-";
    },
    Filter: ({ onChange, filter }) => (
      <DebounceInput
        debounceTimeout={1500}
        minLength={1}
        value={filter ? filter.value : ""}
        onChange={e => onChange(e.target.value)}
      />
    )
  };
};
